import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

export function formatNumber(num, decimals = 2) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(num);
}

export function formatDate(date) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(date));
}

export function calculateWallArea(length, width, height) {
  return 2 * (length + width) * height;
}

export function calculateCeilingArea(length, width) {
  return length * width;
}

export function calculateBaseboardLength(length, width) {
  return 2 * (length + width);
}

// ============ INTERIOR ITEMS ============
// Only Walls/Ceilings default on (near-universal for any room, quantity
// auto-computed from dimensions) - everything else starts unchecked so the
// estimator opts IN to what a specific room actually needs, rather than
// having to notice and un-check items the app guessed at.
export function getDefaultRoomItems(length = 0, width = 0, height = 8) {
  const wallArea = calculateWallArea(length, width, height);
  const ceilingArea = calculateCeilingArea(length, width);
  const baseboardLen = calculateBaseboardLength(length, width);
  const isHighWall = height > 9;

  return [
    // PREP ITEMS
    { item_type: "furniture_treatment", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "window_treatment", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "mask_cover", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "wallpaper_removal", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "rr_hardware", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "prep_woodwork", enabled: false, quantity: 0, coats: 1, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "patch_texture", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "skim_coat", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    // WALLS & CEILINGS (with multi-story support)
    { item_type: "walls_0_9", enabled: !isHighWall, quantity: isHighWall ? 0 : wallArea, coats: 2, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "walls_9_plus", enabled: isHighWall, quantity: isHighWall ? wallArea : 0, coats: 2, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ceiling_paint", enabled: true, quantity: ceilingArea, coats: 1, paint_type: "ceiling_paint", needs_primer: false, prep_level: 3, notes: "" },
    // DOORS
    { item_type: "doors_flat", enabled: false, quantity: 1, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "doors_paneled", enabled: false, quantity: 0, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "doors_french", enabled: false, quantity: 0, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "door_frames", enabled: false, quantity: 1, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    // WINDOWS
    { item_type: "windows_casement", enabled: false, quantity: 0, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "windows_1_1", enabled: false, quantity: 1, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "windows_3_7_pane", enabled: false, quantity: 0, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "windows_8_16_pane", enabled: false, quantity: 0, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    // TRIM
    { item_type: "baseboards", enabled: false, quantity: baseboardLen, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "crown_molding", enabled: false, quantity: baseboardLen, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "stair_rail", enabled: false, quantity: 0, coats: 2, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    // CLOSETS & CABINETRY
    { item_type: "closets", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "cabinetry", enabled: false, quantity: 0, coats: 1, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" },
    // MISC
    { item_type: "daily_setup", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "clean_touchup", enabled: false, quantity: 0, coats: 1, paint_type: "wall_paint", needs_primer: false, prep_level: 3, notes: "" },
  ];
}

// ============ EXTERIOR ITEMS ============
export function getDefaultExteriorItems(length = 0, height = 0) {
  const sqFt = length * height;
  // Linear items should use length value as quantity
  return [
    { item_type: "ext_scraping", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_patch_stucco", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_feather_sand", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_caulking", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_pressure_wash", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_ladder_time", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_spot_prime", enabled: false, quantity: 0, coats: 1, paint_type: "primer", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_mask_windows", enabled: false, quantity: 0, coats: 1, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    // Linear feet items - use length as quantity
    { item_type: "ext_eaves_single", enabled: false, quantity: length, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_eaves_two", enabled: false, quantity: length, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_fascia_single", enabled: false, quantity: length, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_fascia_two", enabled: false, quantity: length, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_gutters", enabled: false, quantity: length, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    // Unit-based items
    { item_type: "ext_shutters", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_win_trim", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_win_2pane", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_door_flat", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_door_paneled", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_garage_door", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    // Square footage items - use sqFt (length * height)
    { item_type: "ext_stucco_single", enabled: false, quantity: sqFt, coats: 2, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_stucco_two", enabled: false, quantity: sqFt, coats: 2, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_siding_single", enabled: false, quantity: sqFt, coats: 2, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_siding_two", enabled: false, quantity: sqFt, coats: 2, paint_type: "exterior_body", needs_primer: false, prep_level: 3, notes: "" },
    // Misc items - wrought iron uses linear feet
    { item_type: "ext_wrought_iron", enabled: false, quantity: length, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "ext_posts", enabled: false, quantity: 0, coats: 2, paint_type: "exterior_trim", needs_primer: false, prep_level: 3, notes: "" },
  ];
}

// ============ DECK ITEMS ============
export function getDefaultDeckItems(length = 0, width = 0) {
  const sqFt = length * width;
  const perimeter = 2 * (length + width);
  return [
    { item_type: "deck_prep_sand", enabled: false, quantity: 0, coats: 1, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_prep_wash", enabled: false, quantity: sqFt, coats: 1, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_prep_strip", enabled: false, quantity: 0, coats: 1, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_prep_repair", enabled: false, quantity: 0, coats: 1, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_floor", enabled: true, quantity: sqFt, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_rails_spindles", enabled: false, quantity: perimeter, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_stairs", enabled: false, quantity: 0, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_posts", enabled: false, quantity: 4, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_lattice", enabled: false, quantity: 0, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_benches", enabled: false, quantity: 0, coats: 1, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_pergola", enabled: false, quantity: 0, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_fence", enabled: false, quantity: 0, coats: 2, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
    { item_type: "deck_misc", enabled: false, quantity: 0, coats: 1, paint_type: "deck_stain", needs_primer: false, prep_level: 3, notes: "" },
  ];
}

// ============ CABINET ITEMS ============
// On-location cabinet refinishing: core linear-ft/per-unit items are priced
// via a tiered $/unit rate (see CabinetEditor + backend rates.py), NOT
// hours x labor_rate - these are the logistics/extras (billed hours) and
// finish-process checklist (unbilled, scheduling-only) side of a job.

// Paint-grade vs. stain-grade pre-enables the relevant finish-process
// checklist items - unlike room items, these are a fixed, always-applicable
// step sequence once a grade is chosen, not a guess about room contents.
export const CABINET_GRADE_TEMPLATES = [
  { value: "paint_grade", label: "Paint-Grade", items: ["cab_sand_raw", "cab_fill_repair", "cab_bonding_primer", "cab_scuff_sand", "cab_conversion_varnish_paint"] },
  { value: "stain_grade", label: "Stain-Grade", items: ["cab_wood_conditioning", "cab_stain_application", "cab_sealer_coat", "cab_conversion_varnish_stain"] },
];

export function getDefaultCabinetCoreItems(pricingMode = "linear_foot") {
  if (pricingMode === "per_unit") {
    return [
      { item_type: "doors", enabled: true, quantity: 0, notes: "" },
      { item_type: "drawers", enabled: true, quantity: 0, notes: "" },
      { item_type: "boxes", enabled: true, quantity: 0, notes: "" },
    ];
  }
  return [
    { item_type: "lower_linear_ft", enabled: true, quantity: 0, notes: "" },
    { item_type: "upper_linear_ft", enabled: true, quantity: 0, notes: "" },
  ];
}

// Logistics/extras rows start disabled (every job's on-location setup is
// different); finish-checklist rows are pre-enabled per finishGrade. Both
// grades' finish rows are always present in the returned list so switching
// grade later just re-enables/disables rows rather than destructively
// dropping any edits the user already made.
export function getDefaultCabinetItems(finishGrade = "paint_grade") {
  const gradeItems = CABINET_GRADE_TEMPLATES.find(t => t.value === finishGrade)?.items || [];
  const logisticsAndExtras = [
    "cab_disassembly", "cab_labeling", "cab_drying_rack", "cab_staging", "cab_spray_booth",
    "cab_rig_setup", "cab_sanding", "cab_transport", "cab_delivery", "cab_reinstall", "cab_breakdown",
    "cab_crown_molding", "cab_built_ins", "cab_extra_shelves", "cab_misc",
  ].map(item_type => ({ item_type, enabled: false, quantity: 0, coats: 1, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "" }));

  const allFinishItems = CABINET_GRADE_TEMPLATES.flatMap(t => t.items).map(item_type => ({
    item_type, enabled: gradeItems.includes(item_type), quantity: 0,
    coats: 1, paint_type: "trim_paint", needs_primer: false, prep_level: 3, notes: "",
  }));

  return [...logisticsAndExtras, ...allFinishItems];
}

export const CABINET_CATEGORIES = {
  logistics: ["cab_disassembly", "cab_labeling", "cab_drying_rack", "cab_staging", "cab_spray_booth", "cab_rig_setup", "cab_sanding", "cab_transport", "cab_delivery", "cab_reinstall", "cab_breakdown"],
  extras: ["cab_crown_molding", "cab_built_ins", "cab_extra_shelves", "cab_misc"],
  finish_paint_grade: ["cab_sand_raw", "cab_fill_repair", "cab_bonding_primer", "cab_scuff_sand", "cab_conversion_varnish_paint"],
  finish_stain_grade: ["cab_wood_conditioning", "cab_stain_application", "cab_sealer_coat", "cab_conversion_varnish_stain"],
};

// Items priced as a direct dollar charge (flat setup fee + per-unit rate)
// rather than hours x labor_rate - coats/paint_type/prep_level/needs_primer
// are meaningless for these, so RoomEditor renders them with a leaner UI.
export const FLAT_PLUS_UNIT_ITEMS = ["patch_texture", "cabinetry"];
export const FLAT_PLUS_UNIT_CAVEATS = {
  patch_texture: "$55/patch covers a basic 4\"x4\" hole with a basic patch only - no sheetrock installed/delivered/repaired, no wall framing. Different texture styles may cost more.",
  cabinetry: "Priced per linear foot at the Cabinet Estimator's Medium-tier rate (Settings → Rates & Coverage). For a full cabinet refinishing job, add a dedicated Cabinet Area instead.",
};

// ============ LABELS ============
export const ITEM_LABELS = {
  // INTERIOR PREP
  furniture_treatment: "Move & Reset Furniture", window_treatment: "Remove & Reset Window Treatment",
  mask_cover: "Mask & Cover", wallpaper_removal: "Wallpaper Removal", rr_hardware: "R&R Hardware & Lighting",
  prep_woodwork: "Prep Woodwork", patch_texture: "Patch & Texture", skim_coat: "Skim Coat",
  // WALLS & CEILINGS
  walls_prime: "Walls: Prime", walls_0_9: "Walls (0'-9')", walls_9_plus: "Walls (9'+)",
  ceiling_prime: "Ceiling: Prime", ceiling_paint: "Ceilings",
  // DOORS
  doors_flat: "Doors: Flat", doors_paneled: "Doors: Paneled", doors_french: "Doors: French", door_frames: "Door Frames/Jams",
  // WINDOWS
  windows_casement: "Windows: Casement", windows_1_1: "Windows: 1/1", windows_3_7_pane: "Windows: 3-7 Pane", windows_8_16_pane: "Windows: 8-16 Pane",
  // TRIM
  baseboards: "Baseboards", crown_molding: "Crown Molding", stair_rail: "Stair Rail System",
  // CLOSETS & CABINETRY
  closets: "Closets", cabinetry: "Cabinetry",
  // MISC
  daily_setup: "Daily Setup/Breakdown", clean_touchup: "Clean & Touchup",
  // EXTERIOR
  ext_scraping: "Scraping & Sanding", ext_patch_stucco: "Patch Stucco", ext_feather_sand: "Feather Sanding",
  ext_caulking: "Caulking", ext_pressure_wash: "Pressure Wash", ext_ladder_time: "Ladder Time",
  ext_spot_prime: "Spot Prime", ext_mask_windows: "Mask Windows & Doors",
  ext_eaves_single: "Eaves (Single Story)", ext_eaves_two: "Eaves (Two Story)",
  ext_fascia_single: "Fascia (Single Story)", ext_fascia_two: "Fascia (Two Story)",
  ext_gutters: "Gutters & Downspouts", ext_shutters: "Shutters", ext_win_trim: "Window Trim",
  ext_win_2pane: "Windows (2 Pane)", ext_door_flat: "Doors (Flat)", ext_door_paneled: "Doors (Paneled)",
  ext_garage_door: "Garage Door", ext_stucco_single: "Stucco (Single Story)", ext_stucco_two: "Stucco (Two Story)",
  ext_siding_single: "Siding (Single Story)", ext_siding_two: "Siding (Two Story)",
  ext_wrought_iron: "Wrought Iron Railing", ext_posts: "Posts/Pillars",
  // DECK
  deck_prep_sand: "Sand Deck", deck_prep_wash: "Pressure Wash Deck", deck_prep_strip: "Strip Old Finish",
  deck_prep_repair: "Deck Repairs", deck_floor: "Deck Floor/Surface", deck_rails_spindles: "Rails & Spindles",
  deck_stairs: "Deck Stairs", deck_posts: "Deck Posts", deck_lattice: "Lattice Work",
  deck_benches: "Built-in Benches", deck_pergola: "Pergola/Arbor", deck_fence: "Fence", deck_misc: "Miscellaneous",
  // CABINET CORE
  lower_linear_ft: "Lower Cabinets (Linear Ft)", upper_linear_ft: "Upper Cabinets (Linear Ft)",
  doors: "Doors", drawers: "Drawers", boxes: "Cabinet Boxes",
  // CABINET LOGISTICS & EXTRAS
  cab_disassembly: "Disassembly & Hardware Removal", cab_labeling: "Hardware/Door Labeling",
  cab_drying_rack: "Build Drying Rack/Hanging System", cab_staging: "Stage Work Area",
  cab_spray_booth: "Set Up On-Site Spray Booth", cab_rig_setup: "Spray Rig Setup/Cleaning/Troubleshooting",
  cab_sanding: "Sanding", cab_transport: "Transport/Travel", cab_delivery: "Delivery",
  cab_reinstall: "Reinstall Doors/Hardware", cab_breakdown: "Breakdown/Tear-Down of On-Site Setup",
  cab_crown_molding: "Crown Molding on Cabinets", cab_built_ins: "Built-In Cabinets",
  cab_extra_shelves: "Extra Shelves", cab_misc: "Miscellaneous",
  // CABINET FINISH PROCESS (tracked for scheduling only, not billed)
  cab_sand_raw: "Sand Raw/Existing Surface", cab_fill_repair: "Fill/Repair Imperfections",
  cab_bonding_primer: "Bonding Primer Coat", cab_scuff_sand: "Scuff Sand",
  cab_conversion_varnish_paint: "Conversion Varnish Topcoat (1-3 coats)",
  cab_wood_conditioning: "Wood Conditioning", cab_stain_application: "Stain Application",
  cab_sealer_coat: "Sealer Coat", cab_conversion_varnish_stain: "Conversion Varnish Topcoat",
};

export const ITEM_UNITS = {
  furniture_treatment: "hrs", window_treatment: "hrs", mask_cover: "hrs", wallpaper_removal: "sq ft",
  rr_hardware: "hrs", prep_woodwork: "hrs", patch_texture: "patches", skim_coat: "sq ft",
  walls_0_9: "sq ft", walls_9_plus: "sq ft", ceiling_paint: "sq ft",
  doors_flat: "units", doors_paneled: "units", doors_french: "units", door_frames: "units",
  windows_casement: "units", windows_1_1: "units", windows_3_7_pane: "units", windows_8_16_pane: "units",
  baseboards: "ln ft", crown_molding: "ln ft", stair_rail: "ln ft",
  closets: "hrs", cabinetry: "ln ft", daily_setup: "hrs", clean_touchup: "hrs",
  ext_scraping: "hrs", ext_patch_stucco: "hrs", ext_feather_sand: "hrs", ext_caulking: "hrs",
  ext_pressure_wash: "hrs", ext_ladder_time: "hrs", ext_spot_prime: "hrs", ext_mask_windows: "units",
  ext_eaves_single: "ln ft", ext_eaves_two: "ln ft", ext_fascia_single: "ln ft", ext_fascia_two: "ln ft",
  ext_gutters: "ln ft", ext_shutters: "units", ext_win_trim: "units", ext_win_2pane: "units",
  ext_door_flat: "units", ext_door_paneled: "units", ext_garage_door: "units",
  ext_stucco_single: "sq ft", ext_stucco_two: "sq ft", ext_siding_single: "sq ft", ext_siding_two: "sq ft",
  ext_wrought_iron: "ln ft", ext_posts: "units",
  deck_prep_sand: "sq ft", deck_prep_wash: "sq ft", deck_prep_strip: "sq ft", deck_prep_repair: "hrs",
  deck_floor: "sq ft", deck_rails_spindles: "ln ft", deck_stairs: "steps", deck_posts: "units",
  deck_lattice: "sq ft", deck_benches: "hrs", deck_pergola: "sq ft", deck_fence: "ln ft", deck_misc: "hrs",
  lower_linear_ft: "ln ft", upper_linear_ft: "ln ft", doors: "units", drawers: "units", boxes: "units",
  cab_disassembly: "hrs", cab_labeling: "hrs", cab_drying_rack: "hrs", cab_staging: "hrs",
  cab_spray_booth: "hrs", cab_rig_setup: "hrs", cab_sanding: "hrs", cab_transport: "hrs",
  cab_delivery: "hrs", cab_reinstall: "hrs", cab_breakdown: "hrs",
  cab_crown_molding: "hrs", cab_built_ins: "hrs", cab_extra_shelves: "hrs", cab_misc: "hrs",
  cab_sand_raw: "hrs", cab_fill_repair: "hrs", cab_bonding_primer: "hrs", cab_scuff_sand: "hrs",
  cab_conversion_varnish_paint: "hrs", cab_wood_conditioning: "hrs", cab_stain_application: "hrs",
  cab_sealer_coat: "hrs", cab_conversion_varnish_stain: "hrs",
};

export const PAINT_TYPES = [
  { value: "primer", label: "Primer" }, { value: "wall_paint", label: "Wall Paint" },
  { value: "ceiling_paint", label: "Ceiling Paint" }, { value: "trim_paint", label: "Trim Paint" },
  { value: "exterior_body", label: "Exterior Body" }, { value: "exterior_trim", label: "Exterior Trim" },
  { value: "deck_stain", label: "Deck Stain" }, { value: "deck_solid", label: "Deck Solid" },
];

// Surface-prep quality level (1-4). Level 1 = best condition, least prep
// needed, fastest production rate. Level 3 is the default/"normal" case.
// Level 4 = worst condition, most prep needed, slowest production rate.
export const PREP_LEVELS = [
  { value: 1, label: "Level 1 - Excellent (minimal prep)" },
  { value: 2, label: "Level 2 - Good" },
  { value: 3, label: "Level 3 - Normal" },
  { value: 4, label: "Level 4 - Poor (heavy prep)" },
];

export const PROJECT_TYPES = [
  { value: "interior", label: "Interior Only" }, { value: "exterior", label: "Exterior Only" },
  { value: "deck", label: "Deck Staining" }, { value: "cabinets", label: "Cabinet Refinishing" },
  { value: "both", label: "Interior & Exterior" },
  { value: "all", label: "Full Service (All)" },
];

export const INTERIOR_CATEGORIES = {
  prep: ["furniture_treatment", "window_treatment", "mask_cover", "wallpaper_removal", "rr_hardware", "prep_woodwork", "patch_texture", "skim_coat"],
  surfaces: ["walls_0_9", "walls_9_plus", "ceiling_paint"],
  doors: ["doors_flat", "doors_paneled", "doors_french", "door_frames"],
  windows: ["windows_casement", "windows_1_1", "windows_3_7_pane", "windows_8_16_pane"],
  trim: ["baseboards", "crown_molding", "stair_rail"],
  other: ["closets", "cabinetry", "daily_setup", "clean_touchup"],
};

export const EXTERIOR_CATEGORIES = {
  prep: ["ext_scraping", "ext_patch_stucco", "ext_feather_sand", "ext_caulking", "ext_pressure_wash", "ext_ladder_time", "ext_spot_prime", "ext_mask_windows"],
  eaves_fascia: ["ext_eaves_single", "ext_eaves_two", "ext_fascia_single", "ext_fascia_two", "ext_gutters"],
  windows_doors: ["ext_shutters", "ext_win_trim", "ext_win_2pane", "ext_door_flat", "ext_door_paneled", "ext_garage_door"],
  body: ["ext_stucco_single", "ext_stucco_two", "ext_siding_single", "ext_siding_two"],
  misc: ["ext_wrought_iron", "ext_posts"],
};

export const DECK_CATEGORIES = {
  prep: ["deck_prep_sand", "deck_prep_wash", "deck_prep_strip", "deck_prep_repair"],
  surfaces: ["deck_floor", "deck_rails_spindles", "deck_stairs", "deck_posts"],
  structures: ["deck_lattice", "deck_benches", "deck_pergola", "deck_fence", "deck_misc"],
};
